import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '@/api/errors'
import { cn } from '@/lib/cn'
import { IconBlock, IconError, IconInfo, IconRefresh } from '@/icons'
import { Button } from './Button'
import { Spinner } from './Spinner'

/**
 * The four states every data-backed view must design (best practices §20.12).
 *
 * A blank card is not an empty state — an empty table has to say why it is empty, or the person
 * looking at it cannot tell a working filter from a broken page.
 */

export interface EmptyStateProps {
  title: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
  className?: string
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className,
      )}
    >
      {icon ? <div className="text-text-muted">{icon}</div> : null}
      <div>
        <p className="text-[0.9375rem] leading-6 font-medium text-text">{title}</p>
        {description ? (
          <p className="mx-auto mt-1 max-w-sm text-[0.8125rem] leading-5 text-text-secondary">
            {description}
          </p>
        ) : null}
      </div>
      {action}
    </div>
  )
}

export interface ErrorStateProps {
  error: unknown
  onRetry?: () => void
  className?: string
}

/**
 * Distinguishes the three failures that look identical if you only print "something went wrong":
 * the server refused you, the server is unreachable, or the server broke.
 */
export function ErrorState({ error, onRetry, className }: ErrorStateProps) {
  const { t } = useTranslation()

  const isForbidden = isApiError(error) && error.isForbidden
  const isOffline = isApiError(error) && error.status === 0
  const isNotFound = isApiError(error) && error.isNotFound

  let title = t('state.errorTitle')
  let description = t('state.errorHint')
  let icon = <IconError size={22} />

  if (isForbidden) {
    title = t('state.forbiddenTitle')
    description = t('state.forbiddenHint')
    icon = <IconBlock size={22} />
  } else if (isOffline) {
    title = t('state.offlineTitle')
    description = t('state.offlineHint')
    icon = <IconInfo size={22} />
  } else if (isNotFound) {
    title = t('state.notFoundTitle')
    description = t('state.notFoundHint')
  } else if (isApiError(error) && error.detail) {
    description = error.detail
  }

  return (
    <EmptyState
      title={title}
      description={description}
      icon={<span className={isForbidden ? 'text-warning' : 'text-danger'}>{icon}</span>}
      /* A 403 is never retried — the answer will not change, and retrying is how a UI
         hammers a backend it can never satisfy (best practices §5.8). */
      action={
        onRetry && !isForbidden ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<IconRefresh size={14} />}
            onClick={onRetry}
          >
            {t('common.retry')}
          </Button>
        ) : undefined
      }
      {...(className ? { className } : {})}
    />
  )
}

export function LoadingState({ className }: { className?: string }) {
  const { t } = useTranslation()

  return (
    <div className={cn('flex items-center justify-center gap-2 px-6 py-14', className)}>
      <Spinner size={18} label={t('common.loading')} className="text-text-muted" />
      <span className="text-[0.8125rem] text-text-secondary">{t('common.loading')}</span>
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        'rounded bg-surface-sunken motion-safe:animate-pulse',
        className ?? 'h-4 w-full',
      )}
    />
  )
}

/** A placeholder shaped like the table it replaces, so the layout does not jump on load. */
export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-border" aria-hidden>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-4', columnIndex === 0 ? 'w-1/4' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
